/**
 * 绝对路径: D:/XXY/MeiShan_Project/frontend/shared/api/catalogsManagement.ts
 * 版本: v2.0
 * 创建日期: 2025-12-09
 * 更新日期: 2025-12-09
 * 创建人: AI Assistant
 * 
 * 常量目录管理 API（CRUD 操作）
 * 
 * 后端接口来源：/api/v1/constants/ (catalogs/api/urls.py)
 * 
 * 支持的常量类型：
 * - 商户相关：商户品牌、菜系、菜品分类、辣度等级
 * - 企业相关：企业类型、企业行业、员工角色标签
 * - 业务配置：服务类别、餐段类别、平台能力、订单状态、配送状态
 * - 工单相关：投诉分类、投诉类型
 * - 系统相关：行政区划、节假日
 */

import { httpClient } from './http'

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value)
}

function toRecord(value: unknown): Record<string, unknown> {
  return isRecord(value) ? value : {}
}

// ========================================
// 类型定义
// ========================================

/**
 * 目录项基础接口
 */
export interface CatalogItemBase {
  id: number
  code: string
  label: string
  description?: string
  is_active: boolean
  sort_order: number
  status?: 'PENDING' | 'APPROVED' | 'REJECTED'
  created_at?: string
  updated_at?: string
  created_by?: number
  created_by_name?: string
  approved_by?: number
  approved_by_name?: string
  deprecated_at?: string | null
  replaced_by?: number | null
}

/**
 * 商户品牌
 */
export interface MerchantBrand extends CatalogItemBase {
  logo_url?: string
}

/**
 * 菜系
 */
export interface Cuisine extends CatalogItemBase {}

/**
 * 菜品分类（支持树形结构）
 */
export interface DishCategory extends CatalogItemBase {
  group: 'combo' | 'single' | 'materials'
  parent?: number | null
  parent_name?: string
  children?: DishCategory[]
}

/**
 * 服务类别（消费类型）
 */
export interface ServiceType extends CatalogItemBase {
  exclusive_with?: string[]
  capabilities?: string[]
  capability_defaults?: Record<string, boolean>
}

/**
 * 餐段类别（支持树形结构）
 */
export interface MealSegment extends CatalogItemBase {
  parent?: number | null
  parent_name?: string
  service_start_time?: string
  service_end_time?: string
  children?: MealSegment[]
}

/**
 * 平台能力
 */
export interface Capability extends CatalogItemBase {
  capability_type?: string
}

/**
 * 企业类型
 */
export interface EnterpriseType extends CatalogItemBase {}

/**
 * 企业行业
 */
export interface EnterpriseIndustry extends CatalogItemBase {}

/**
 * 订单状态（只读）
 */
export interface OrderStatus extends CatalogItemBase {
  color?: string
  next_statuses?: string[]
}

/**
 * 配送状态（只读）
 */
export interface DeliveryStatus extends CatalogItemBase {
  color?: string
}

/**
 * 投诉分类（一级）
 */
export interface ComplaintCategory extends CatalogItemBase {
  children?: ComplaintType[]
}

/**
 * 投诉类型（二级）
 */
export interface ComplaintType extends CatalogItemBase {
  category?: number | null
  category_name?: string
  sla_hours?: number
}

/**
 * 员工角色标签
 */
export interface EmployeeRoleTag extends CatalogItemBase {}

/**
 * 辣度等级
 */
export interface SpicinessLevel {
  code: string
  label: string
  value: number
  order: number
}

/**
 * 行政区划
 */
export interface AdminDivision extends CatalogItemBase {
  level: 'province' | 'city' | 'district'
  parent?: string | null
  children?: AdminDivision[]
}

/**
 * 节假日
 */
export interface Holiday {
  date: string
  label: string
  is_working_day: boolean
  is_legal_holiday: boolean
}

// ========================================
// 输入类型
// ========================================

export interface CatalogItemInput {
  label: string
  description?: string
  is_active?: boolean
  sort_order?: number
}

export interface DishCategoryInput extends CatalogItemInput {
  group?: 'combo' | 'single' | 'materials'
  parent?: number | null
}

export interface ServiceTypeInput extends CatalogItemInput {
  exclusive_with?: string[]
  capabilities?: string[]
  capability_defaults?: Record<string, boolean>
}

export interface MealSegmentInput extends CatalogItemInput {
  parent?: number | null
  service_start_time?: string
  service_end_time?: string
}

export interface ComplaintTypeInput extends CatalogItemInput {
  category?: number | null
  sla_hours?: number
}

// ========================================
// 查询参数
// ========================================

export interface CatalogQueryParams {
  page?: number
  page_size?: number
  search?: string
  is_active?: boolean
  status?: string
  ordering?: string
}

// ========================================
// 响应类型
// ========================================

export interface PaginatedResponse<T> {
  count: number
  next: string | null
  previous: string | null
  results: T[]
}

// ========================================
// 通用目录管理类
// ========================================

class CatalogManagementApi<T extends CatalogItemBase, TInput extends CatalogItemInput = CatalogItemInput> {
  private endpoint: string
  private readonly: boolean

  constructor(endpoint: string, readonly = false) {
    this.endpoint = endpoint
    this.readonly = readonly
  }

  /**
   * 获取列表
   */
  async list(params?: CatalogQueryParams): Promise<PaginatedResponse<T>> {
    const response = await httpClient.getAxiosInstance().get<PaginatedResponse<T>>(
      `constants/${this.endpoint}/`,
      { params }
    )
    return response.data
  }

  /**
   * 获取所有（不分页）
   */
  async listAll(activeOnly = true): Promise<T[]> {
    const params: CatalogQueryParams = { page_size: 10000 }
    if (activeOnly) {
      params.is_active = true
    }
    const response = await this.list(params)
    return response.results
  }

  /**
   * 获取树形结构（适用于菜品分类、餐段类别）
   */
  async tree(activeOnly = true): Promise<T[]> {
    const params: Record<string, unknown> = {}
    if (activeOnly) {
      params.is_active = true
    }
    const response = await httpClient.getAxiosInstance().get<T[]>(
      `constants/${this.endpoint}/tree/`,
      { params }
    )
    return response.data
  }

  /**
   * 获取详情
   */
  async get(id: number | string): Promise<T> {
    const response = await httpClient.getAxiosInstance().get<T>(
      `constants/${this.endpoint}/${id}/`
    )
    return response.data
  }

  /**
   * 创建（只读目录不支持）
   */
  async create(data: TInput): Promise<T> {
    if (this.readonly) {
      throw new Error(`${this.endpoint} 是只读目录，不支持创建`)
    }
    const response = await httpClient.getAxiosInstance().post<T>(
      `constants/${this.endpoint}/`,
      data
    )
    return response.data
  }

  /**
   * 更新（只读目录不支持）
   */
  async update(id: number | string, data: Partial<TInput>): Promise<T> {
    if (this.readonly) {
      throw new Error(`${this.endpoint} 是只读目录，不支持更新`)
    }
    const response = await httpClient.getAxiosInstance().patch<T>(
      `constants/${this.endpoint}/${id}/`,
      data
    )
    return response.data
  }

  /**
   * 删除（只读目录不支持）
   */
  async delete(id: number | string): Promise<void> {
    if (this.readonly) {
      throw new Error(`${this.endpoint} 是只读目录，不支持删除`)
    }
    await httpClient.getAxiosInstance().delete(`constants/${this.endpoint}/${id}/`)
  }

  /**
   * 批量更新状态
   */
  async batchUpdateStatus(ids: (number | string)[], isActive: boolean): Promise<void> {
    if (this.readonly) {
      throw new Error(`${this.endpoint} 是只读目录，不支持更新`)
    }
    await Promise.all(
      ids.map(id => this.update(id, { is_active: isActive } as Partial<TInput>))
    )
  }

  /**
   * 审批通过
   */
  async approve(id: number | string): Promise<T> {
    const response = await httpClient.getAxiosInstance().post<T>(
      `constants/${this.endpoint}/${id}/approve/`
    )
    return response.data
  }

  /**
   * 审批拒绝
   */
  async reject(id: number | string, reason?: string): Promise<T> {
    const response = await httpClient.getAxiosInstance().post<T>(
      `constants/${this.endpoint}/${id}/reject/`,
      { reason }
    )
    return response.data
  }

  /**
   * 是否只读
   */
  isReadonly(): boolean {
    return this.readonly
  }
}

// ========================================
// 导出各目录管理 API 实例
// ========================================

// 商户相关（可写）
export const merchantBrandsApi = new CatalogManagementApi<MerchantBrand>('merchant-brands')
export const cuisinesApi = new CatalogManagementApi<Cuisine>('cuisines')
export const dishCategoriesApi = new CatalogManagementApi<DishCategory, DishCategoryInput>('dish-categories')

// 企业相关（可写）
export const enterpriseTypesApi = new CatalogManagementApi<EnterpriseType>('enterprise-types')
export const enterpriseIndustriesApi = new CatalogManagementApi<EnterpriseIndustry>('enterprise-industries')

// 业务配置（可写）
export const serviceTypesApi = new CatalogManagementApi<ServiceType, ServiceTypeInput>('service-types')
export const mealSegmentsApi = new CatalogManagementApi<MealSegment, MealSegmentInput>('meal-segments')
export const capabilitiesApi = new CatalogManagementApi<Capability>('capabilities')

// 订单相关（只读）
export const orderStatusesApi = new CatalogManagementApi<OrderStatus>('order-statuses', true)
export const deliveryStatusesApi = new CatalogManagementApi<DeliveryStatus>('delivery-statuses', true)

// 工单相关（可写）
export const complaintCategoriesApi = new CatalogManagementApi<ComplaintCategory>('complaint-categories')
export const complaintTypesApi = new CatalogManagementApi<ComplaintType, ComplaintTypeInput>('complaint-types')

// ========================================
// 只读常量 API（非 ViewSet，不支持 CRUD）
// ========================================

/**
 * 辣度等级（只读）
 */
export async function getSpicinessLevels(): Promise<SpicinessLevel[]> {
  const response = await httpClient.getAxiosInstance().get('constants/spiciness-levels/')
  const raw: unknown = response.data
  const nested = isRecord(raw) ? (toRecord(raw).data ?? raw) : raw
  const n = toRecord(nested)
  const items = Array.isArray(n.items) ? (n.items as unknown[]) : (Array.isArray(nested) ? nested : [])
  return items.map((it: unknown) => {
    const r = toRecord(it)
    return {
      code: String(r.code ?? ''),
      label: String(r.label ?? ''),
      value: Number(r.value ?? 0),
      order: Number(r.order ?? 0)
    }
  })
}

/**
 * 员工角色标签（只读）
 */
export async function getEmployeeRoleTags(activeOnly = true): Promise<EmployeeRoleTag[]> {
  const params: Record<string, unknown> = {}
  if (activeOnly) {
    params.is_active = true
  }
  const response = await httpClient.getAxiosInstance().get('constants/employee-role-tags/', { params })
  const raw = response.data
  const items = Array.isArray(raw) ? raw : (raw?.results || raw?.data || raw?.items || [])
  return items
}

/**
 * 行政区划（只读）
 */
export async function getAdminDivisions(params?: { level?: string; parent?: string }): Promise<AdminDivision[]> {
  const response = await httpClient.getAxiosInstance().get('constants/admin-divisions/', { params })
  const raw = response.data
  const items = Array.isArray(raw) ? raw : (raw?.results || raw?.data || raw?.items || [])
  return items
}

/**
 * 节假日（只读）
 */
export async function getHolidaysByYear(year: number): Promise<Holiday[]> {
  const response = await httpClient.getAxiosInstance().get(`constants/holidays/${year}/`)
  const raw: unknown = response.data
  const dataWrapper = isRecord(raw) ? (toRecord(raw).data ?? raw) : raw
  const dw = toRecord(dataWrapper)
  const holidays = Array.isArray(dw.holidays) ? (dw.holidays as unknown[]) : []
  const workdays = Array.isArray(dw.workdays) ? (dw.workdays as unknown[]) : []
  
  const result: Holiday[] = []
  
  holidays.forEach((it: unknown) => {
    const r = toRecord(it)
    result.push({
      date: String(r.date || r.day || ''),
      label: String(r.label || r.name || '节假日'),
      is_working_day: false,
      is_legal_holiday: true
    })
  })
  
  workdays.forEach((it: unknown) => {
    const r = toRecord(it)
    result.push({
      date: String(r.date || r.day || ''),
      label: String(r.label || r.name || '调休上班'),
      is_working_day: true,
      is_legal_holiday: false
    })
  })
  
  return result
}

// ========================================
// 常量分组配置（用于前端 Tab 展示）
// ========================================

export interface CatalogGroup {
  key: string
  label: string
  icon: string
  catalogs: CatalogConfig[]
}

export interface CatalogConfig {
  key: string
  label: string
  endpoint: string
  api: CatalogManagementApi<CatalogItemBase, CatalogItemInput> | null
  readonly: boolean
  hasTree?: boolean
  description?: string
}

function asCatalogConfigApi<T extends CatalogItemBase, TInput extends CatalogItemInput>(
  api: CatalogManagementApi<T, TInput>
): CatalogManagementApi<CatalogItemBase, CatalogItemInput> {
  return api as unknown as CatalogManagementApi<CatalogItemBase, CatalogItemInput>
}

/**
 * 常量分组配置
 */
export const CATALOG_GROUPS: CatalogGroup[] = [
  {
    key: 'merchant',
    label: '商户相关',
    icon: 'Store',
    catalogs: [
      { key: 'merchant-brands', label: '商户品牌', endpoint: 'merchant-brands', api: asCatalogConfigApi(merchantBrandsApi), readonly: false, description: '商户/餐厅品牌' },
      { key: 'cuisines', label: '菜系管理', endpoint: 'cuisines', api: asCatalogConfigApi(cuisinesApi), readonly: false, description: '川菜、粤菜、湘菜等' },
      { key: 'dish-categories', label: '菜品分类', endpoint: 'dish-categories', api: asCatalogConfigApi(dishCategoriesApi), readonly: false, hasTree: true, description: '主菜、单品、自选材料等' },
    ]
  },
  {
    key: 'enterprise',
    label: '企业相关',
    icon: 'OfficeBuilding',
    catalogs: [
      { key: 'enterprise-types', label: '企业类型', endpoint: 'enterprise-types', api: asCatalogConfigApi(enterpriseTypesApi), readonly: false, description: '国企、民营、外企等' },
      { key: 'enterprise-industries', label: '企业行业', endpoint: 'enterprise-industries', api: asCatalogConfigApi(enterpriseIndustriesApi), readonly: false, description: '互联网、金融、制造业等' },
    ]
  },
  {
    key: 'business',
    label: '业务配置',
    icon: 'Setting',
    catalogs: [
      { key: 'service-types', label: '服务类别', endpoint: 'service-types', api: asCatalogConfigApi(serviceTypesApi), readonly: false, description: '线上订餐、到店消费等' },
      { key: 'meal-segments', label: '餐段类别', endpoint: 'meal-segments', api: asCatalogConfigApi(mealSegmentsApi), readonly: false, hasTree: true, description: '早餐、午餐、晚餐等' },
      { key: 'capabilities', label: '平台能力', endpoint: 'capabilities', api: asCatalogConfigApi(capabilitiesApi), readonly: false, description: '餐柜、打印、配送等' },
    ]
  },
  {
    key: 'order',
    label: '订单相关',
    icon: 'Document',
    catalogs: [
      { key: 'order-statuses', label: '订单状态', endpoint: 'order-statuses', api: asCatalogConfigApi(orderStatusesApi), readonly: true, description: '待支付、待接单、已完成等' },
      { key: 'delivery-statuses', label: '配送状态', endpoint: 'delivery-statuses', api: asCatalogConfigApi(deliveryStatusesApi), readonly: true, description: '待配送、配送中、已送达等' },
    ]
  },
  {
    key: 'ticket',
    label: '工单相关',
    icon: 'Tickets',
    catalogs: [
      { key: 'complaint-categories', label: '投诉分类', endpoint: 'complaint-categories', api: asCatalogConfigApi(complaintCategoriesApi), readonly: false, description: '食品质量、配送问题等' },
      { key: 'complaint-types', label: '投诉类型', endpoint: 'complaint-types', api: asCatalogConfigApi(complaintTypesApi), readonly: false, description: '异物、漏餐、迟到等' },
    ]
  },
  {
    key: 'system',
    label: '系统配置',
    icon: 'Tools',
    catalogs: [
      { key: 'admin-divisions', label: '行政区划', endpoint: 'admin-divisions', api: null, readonly: true, description: '省市区三级联动' },
      { key: 'spiciness-levels', label: '辣度等级', endpoint: 'spiciness-levels', api: null, readonly: true, description: '不辣、微辣、中辣等' },
      { key: 'employee-role-tags', label: '员工角色标签', endpoint: 'employee-role-tags', api: null, readonly: true, description: '实习生、正式员工等' },
    ]
  }
]

/**
 * 根据 key 获取常量配置
 */
export function getCatalogConfigByKey(key: string): CatalogConfig | undefined {
  for (const group of CATALOG_GROUPS) {
    const found = group.catalogs.find(c => c.key === key)
    if (found) return found
  }
  return undefined
}

/**
 * 获取所有可写的常量配置
 */
export function getWritableCatalogs(): CatalogConfig[] {
  const result: CatalogConfig[] = []
  for (const group of CATALOG_GROUPS) {
    result.push(...group.catalogs.filter(c => !c.readonly))
  }
  return result
}

// ========================================
// 统一导出
// ========================================

export const catalogsManagementApi = {
  // 商户相关
  merchantBrands: merchantBrandsApi,
  cuisines: cuisinesApi,
  dishCategories: dishCategoriesApi,
  
  // 企业相关
  enterpriseTypes: enterpriseTypesApi,
  enterpriseIndustries: enterpriseIndustriesApi,
  
  // 业务配置
  serviceTypes: serviceTypesApi,
  mealSegments: mealSegmentsApi,
  capabilities: capabilitiesApi,
  
  // 订单相关（只读）
  orderStatuses: orderStatusesApi,
  deliveryStatuses: deliveryStatusesApi,
  
  // 工单相关
  complaintCategories: complaintCategoriesApi,
  complaintTypes: complaintTypesApi,
  
  // 只读常量函数
  getSpicinessLevels,
  getEmployeeRoleTags,
  getAdminDivisions,
  getHolidaysByYear,
  
  // 配置
  CATALOG_GROUPS,
  getCatalogConfigByKey,
  getWritableCatalogs,
}
